import { useRef } from 'react'
import { Input } from 'components/ui/input'
import { Button } from 'components/ui/button'
import { Label } from 'components/ui/label'
export const validMailAddress = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
export const splitMailRecipients = (value: string) => value.split(/[,\r\n]+/).map(item => item.trim()).filter(Boolean)
export interface RecipientEditor { buffer: string; editing: number | null; selected: number | null; error: string }
export const emptyRecipientEditor = (): RecipientEditor => ({ buffer: '', editing: null, selected: null, error: '' })
interface Props {
  id: string; label: string; tokens: string[]; editor: RecipientEditor; disabled: boolean
  onTokens: (tokens: string[]) => void; onEditor: (editor: RecipientEditor) => void
  inputRef?: (input: HTMLInputElement | null) => void
}
export function MailRecipientInput({ id, label, tokens, editor, disabled, onTokens, onEditor, inputRef }: Props) {
  const input = useRef<HTMLInputElement | null>(null)
  const commit = (parts = splitMailRecipients(editor.buffer)) => {
    if (!parts.length) return
    const next = [...tokens]
    if (editor.editing === null) next.push(...parts)
    else next.splice(editor.editing, 1, ...parts)
    onTokens(next); onEditor(emptyRecipientEditor())
  }
  const remove = (index: number) => {
    onTokens(tokens.filter((_, i) => i !== index))
    onEditor({ ...editor, selected: null, editing: editor.editing === index ? null : editor.editing !== null && editor.editing > index ? editor.editing - 1 : editor.editing })
  }
  return <div className="space-y-1 min-w-0"><Label htmlFor={id}>{label}</Label>
    <div className="flex flex-wrap gap-1 rounded border p-2 min-w-0">
      {tokens.map((token, index) => <span key={index} className="inline-flex max-w-full flex-wrap items-center gap-1 rounded border px-2 py-1" aria-invalid={!validMailAddress(token)} data-selected={editor.selected === index || undefined}>
        <span className="break-all">{token}</span>{!validMailAddress(token) && <span className="text-red-700">Indirizzo non valido</span>}
        <Button size="sm" variant="ghost" disabled={disabled} aria-label={`Modifica ${token}`} onClick={() => {
          if (editor.buffer) { onEditor({ ...editor, error: 'Conferma o correggi prima il testo nel campo.' }); input.current?.focus(); return }
          onEditor({ buffer: token, editing: index, selected: null, error: '' }); input.current?.focus()
        }}>Modifica</Button>
        <Button size="sm" variant="ghost" disabled={disabled} aria-label={`Rimuovi ${token}`} onClick={() => remove(index)}>×</Button>
      </span>)}
      <Input id={id} ref={node => { input.current = node; inputRef?.(node) }} className="min-w-32 flex-1" value={editor.buffer} disabled={disabled}
        aria-invalid={!!editor.buffer && !validMailAddress(editor.buffer.trim())} aria-describedby={editor.error ? `${id}-error` : undefined}
        placeholder="Aggiungi indirizzo" onChange={event => onEditor({ ...editor, buffer: event.target.value, selected: null, error: '' })}
        onPaste={event => {
          const text = event.clipboardData.getData('text')
          if (!/[,\r\n]/.test(text)) return
          event.preventDefault()
          const field = event.currentTarget
          commit(splitMailRecipients(editor.buffer.slice(0, field.selectionStart ?? editor.buffer.length) + text + editor.buffer.slice(field.selectionEnd ?? editor.buffer.length)))
        }}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ',') { event.preventDefault(); commit(); return }
          if (event.key === 'Escape') { onEditor({ ...editor, selected: null }); return }
          if (event.key === 'Backspace' && !editor.buffer && tokens.length) {
            event.preventDefault()
            if (editor.selected === tokens.length - 1) remove(tokens.length - 1)
            else onEditor({ ...editor, selected: tokens.length - 1 })
          }
        }} />
    </div>{editor.error && <p id={`${id}-error`} role="alert">{editor.error}</p>}
  </div>
}
